import React from 'react';
import { injectIntl } from 'react-intl';
import PropTypes from 'prop-types';
import Parser from 'html-react-parser';
import Constants from 'constants/Constants';
import LoginStore from 'stores/LoginStore';
import styled from 'styled-components';
import EditableObjectHandle from '../Common/EditableObjectHandler';
import IntlObject from '../Common/IntlObject';

// Full-width wrapper with background image
// Uses pseudo-element for full-width background that breaks out of container
const HeroContactWrapper = styled.section`
  position: relative;
  width: 100%;
  min-height: 500px;

  /* Full-width background layer */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 100vw;
    height: 100%;
    background-image: url(${props => props.backgroundImage});
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    z-index: 0;
    pointer-events: none;
  }

  /* Dark overlay */
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 100vw;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: 1;
    pointer-events: none;
  }
`;

// Container for content (max-width constrained)
const ContentContainer = styled.div`
  position: relative;
  z-index: 2;
  max-width: 1200px;
  margin: 0 auto;
  padding: 60px 20px;
  display: flex;
  flex-wrap: wrap;
  align-items: stretch;
  min-height: 500px;

  @media (max-width: 991px) {
    flex-direction: column;
    padding: 40px 15px;
    min-height: auto;
  }
`;

// Left Column - Text content over background
const LeftColumn = styled.div`
  flex: 0 0 58.333333%;
  max-width: 58.333333%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding-right: 40px;
  color: var(--color-text-light, #ffffff);

  @media (max-width: 991px) {
    flex: 0 0 100%;
    max-width: 100%;
    padding-right: 0;
    padding-bottom: 30px;
    text-align: center;
  }
`;

// Inner row for Portrait + Text layout
const LeftInnerRow = styled.div`
  display: flex;
  align-items: center;
  gap: 30px;

  @media (max-width: 991px) {
    flex-direction: column;
    gap: 20px;
  }
`;

// Portrait container - 50% width
const PortraitColumn = styled.div`
  flex: 0 0 50%;
  max-width: 50%;

  @media (max-width: 991px) {
    flex: 0 0 100%;
    max-width: 280px;
  }
`;

// Portrait image wrapper - larger display
const PortraitWrapper = styled.div`
  width: 100%;
  aspect-ratio: 4 / 5;
  border-radius: 6px;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center 20%;
  }
`;

// Text column next to portrait - 50% width
const TextColumn = styled.div`
  flex: 0 0 50%;
  max-width: 50%;
  display: flex;
  flex-direction: column;
  justify-content: center;

  @media (max-width: 991px) {
    flex: 0 0 100%;
    max-width: 100%;
  }
`;

// Editable content area - uses Hero font CSS variables
const HeroContent = styled.div`
  & h1, & h2 {
    font-family: var(--font-hero-english-font-family, 'Playfair Display'), serif;
    font-size: calc(2.5rem * var(--font-hero-english-font-size, 1));
    font-weight: var(--font-hero-english-font-weight, 700);
    letter-spacing: calc(var(--font-hero-english-letter-spacing, 0) * 1em);
    line-height: var(--font-hero-english-line-height, 1.2);
    margin-bottom: 15px;
    color: var(--color-text-light, #ffffff) !important;

    @media (max-width: 991px) {
      font-size: calc(1.8rem * var(--font-hero-english-font-size, 1));
    }
  }

  & p {
    font-family: var(--font-hero-korean-font-family, 'Noto Sans KR'), sans-serif;
    font-size: calc(1rem * var(--font-hero-korean-font-size, 1));
    font-weight: var(--font-hero-korean-font-weight, 400);
    letter-spacing: calc(var(--font-hero-korean-letter-spacing, 0) * 1em);
    line-height: var(--font-hero-korean-line-height, 1.6);
    margin-bottom: 20px;
    opacity: 0.9;
    color: var(--color-text-light, #ffffff) !important;
  }

  & .phone-info {
    margin-top: 20px;
  }

  & .phone-label {
    font-size: calc(0.9rem * var(--font-hero-korean-font-size, 1));
    opacity: 0.8;
    margin-bottom: 5px;
  }

  & .phone-number {
    font-family: var(--font-hero-english-font-family, 'Playfair Display'), serif;
    font-size: calc(1.8rem * var(--font-hero-english-font-size, 1));
    font-weight: var(--font-hero-english-font-weight, 700);
    margin-bottom: 15px;
  }
`;

const ConsultButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background-color: rgba(255, 255, 255, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.5);
  border-radius: 4px;
  color: var(--color-text-light, #ffffff) !important;
  font-weight: 500;
  text-decoration: none;
  transition: all 0.3s ease;

  &:hover {
    background-color: rgba(255, 255, 255, 0.25);
    color: var(--color-text-light, #ffffff) !important;
    text-decoration: none;
  }

  @media (max-width: 991px) {
    justify-content: center;
  }
`;

// Right Column - Form with dark overlay
const RightColumn = styled.div`
  flex: 0 0 41.666667%;
  max-width: 41.666667%;
  display: flex;
  align-items: center;
  justify-content: center;

  @media (max-width: 991px) {
    flex: 0 0 100%;
    max-width: 100%;
  }
`;

const FormBox = styled.div`
  width: 100%;
  max-width: 400px;
  background-color: rgba(30, 41, 59, 0.95);
  border-radius: 8px;
  padding: 35px 30px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);

  @media (max-width: 991px) {
    max-width: 100%;
  }
`;

const FormHeader = styled.h3`
  font-size: 1.4rem;
  font-weight: 600;
  margin-bottom: 25px;
  color: var(--color-text-light, #ffffff) !important;
`;

const FormRow = styled.div`
  display: flex;
  gap: 15px;
  margin-bottom: 0;

  @media (max-width: 600px) {
    flex-direction: column;
    gap: 0;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 18px;
  flex: 1;
`;

const FormLabel = styled.label`
  display: block;
  font-size: 0.85rem;
  font-weight: 500;
  margin-bottom: 6px;
  color: var(--color-text-light, #ffffff);
`;

const FormInput = styled.input`
  width: 100%;
  padding: 12px 14px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 4px;
  font-size: 0.9rem;
  background-color: rgba(255, 255, 255, 0.1);
  color: var(--color-text-light, #ffffff);
  transition: border-color 0.2s ease, background-color 0.2s ease;

  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }

  &:focus {
    outline: none;
    border-color: var(--color-secondary, #d4a853);
    background-color: rgba(255, 255, 255, 0.15);
  }
`;

const FormTextarea = styled.textarea`
  width: 100%;
  padding: 12px 14px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 4px;
  font-size: 0.9rem;
  background-color: rgba(255, 255, 255, 0.1);
  color: var(--color-text-light, #ffffff);
  resize: vertical;
  min-height: 80px;
  transition: border-color 0.2s ease, background-color 0.2s ease;

  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }

  &:focus {
    outline: none;
    border-color: var(--color-secondary, #d4a853);
    background-color: rgba(255, 255, 255, 0.15);
  }
`;

const SubmitButton = styled.button`
  width: 100%;
  padding: 14px 20px;
  background-color: var(--color-secondary, #d4a853);
  border: none;
  border-radius: 4px;
  color: var(--color-text, #1e293b);
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s ease, transform 0.1s ease;
  margin-top: 10px;

  &:hover {
    background-color: var(--color-secondary-hover, #c49a48);
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
`;

const PlaceholderBg = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, #1e3a5f 0%, #2d4a6f 50%, #3d5a7f 100%);
  z-index: 0;
`;

class HeroContactSection extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      authInfo: LoginStore.authInfo,
    };

    this.onChange = this.onChange.bind(this);
  }

  onChange() {
    this.setState({ authInfo: LoginStore.authInfo });
  }

  componentDidMount() {
    LoginStore.addChangeListener(this.onChange);
  }

  componentWillUnmount() {
    LoginStore.removeChangeListener(this.onChange);
  }

  render() {
    const intl = this.props.intl;
    const currentLocale = intl.locale || 'ko';

    const isLogin = this.state.authInfo
      ? this.state.authInfo.status === Constants.AUTHED
      : false;

    const defaultObjects = {
      backgroundImage: '',
      portraitImage: '',
      formspreeEndpoint: '',
      htmlContentKo: `<h2>Your Trusted Partner in Real Estate</h2>
<p>믿고 맡기는 부동산 전문가</p>
<div class="phone-info">
  <p class="phone-label">직통문의</p>
  <p class="phone-number">201-819-2660</p>
</div>`,
      htmlContentEn: `<h2>Your Trusted Partner in Real Estate</h2>
<p>Your Trusted Real Estate Expert</p>
<div class="phone-info">
  <p class="phone-label">Direct Inquiry</p>
  <p class="phone-number">201-819-2660</p>
</div>`,
      formTitleKo: 'Contact Form',
      formTitleEn: 'Contact Form',
      showConsultButton: 'true',
    };

    // Merge options with defaults
    const hasOptions = this.props.options && Object.keys(this.props.options).length > 0;
    const mergedObjects = hasOptions ? { ...defaultObjects, ...this.props.options } : defaultObjects;

    const backgroundImage = mergedObjects.backgroundImage || this.props.backgroundImage || '';
    const portraitImage = mergedObjects.portraitImage || '';
    const htmlContent = currentLocale === 'en' ? mergedObjects.htmlContentEn : mergedObjects.htmlContentKo;
    const formTitle = currentLocale === 'en' ? mergedObjects.formTitleEn : mergedObjects.formTitleKo;
    const showConsultButton = mergedObjects.showConsultButton !== 'false';

    return (
      <HeroContactWrapper
        className={this.props.containerClass}
        backgroundImage={backgroundImage}
      >
        {/* Placeholder gradient if no background image */}
        {!backgroundImage && <PlaceholderBg />}

        <EditableObjectHandle
          id={this.props.id}
          objects={mergedObjects}
          defaultObjects={defaultObjects}
          isAdmin={isLogin}
          componentTitle="HeroContactSection"
        />

        <ContentContainer>
          {/* Left Column - Portrait + Text Content in 2-column layout */}
          <LeftColumn>
            <LeftInnerRow>
              {/* Portrait Image Column */}
              {portraitImage && (
                <PortraitColumn>
                  <PortraitWrapper>
                    <img src={portraitImage} alt="Portrait" />
                  </PortraitWrapper>
                </PortraitColumn>
              )}

              {/* Text Content Column */}
              <TextColumn>
                <HeroContent>
                  {htmlContent ? Parser(htmlContent) : (
                    <>
                      <h2>Your Trusted Partner in Real Estate</h2>
                      <p>믿고 맡기는 부동산 전문가</p>
                    </>
                  )}
                </HeroContent>

                {showConsultButton && this.props.googleFormLink && (
                  <ConsultButton
                    href={this.props.googleFormLink}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {intl.formatMessage({ id: 'header.contact.consultation' })}
                    <img
                      src="https://img.icons8.com/fluency-systems-filled/16/FFFFFF/more-than.png"
                      alt="more"
                    />
                  </ConsultButton>
                )}
              </TextColumn>
            </LeftInnerRow>
          </LeftColumn>

          {/* Right Column - Contact Form */}
          <RightColumn>
            <FormBox>
              <form action={mergedObjects.formspreeEndpoint || ''} method="post">
                <FormHeader>
                  <IntlObject keycode="contact.form.header" returnType="span" />
                </FormHeader>

                <FormGroup>
                  <FormLabel>
                    <IntlObject keycode="contact.form.name.label" returnType="span" />*
                  </FormLabel>
                  <FormInput
                    id="name"
                    name="name"
                    type="text"
                    required
                    placeholder={intl.formatMessage({ id: 'contact.form.name.placeholder' })}
                  />
                </FormGroup>

                <FormRow>
                  <FormGroup>
                    <FormLabel>
                      <IntlObject keycode="contact.form.email.label" returnType="span" />*
                    </FormLabel>
                    <FormInput
                      id="email"
                      name="email"
                      type="email"
                      required
                      placeholder={intl.formatMessage({ id: 'contact.form.email.placeholder' })}
                    />
                  </FormGroup>

                  <FormGroup>
                    <FormLabel>
                      <IntlObject keycode="contact.form.phone.label" returnType="span" />*
                    </FormLabel>
                    <FormInput
                      id="phone"
                      name="phone"
                      type="text"
                      required
                      placeholder={intl.formatMessage({ id: 'contact.form.phone.placeholder' })}
                    />
                  </FormGroup>
                </FormRow>

                <FormGroup>
                  <FormLabel>
                    <IntlObject keycode="contact.form.message.label" returnType="span" />
                  </FormLabel>
                  <FormTextarea
                    id="message"
                    name="message"
                    placeholder={intl.formatMessage({ id: 'contact.form.message.placeholder' })}
                  />
                </FormGroup>

                <SubmitButton type="submit">
                  <IntlObject keycode="contact.form.submit" returnType="span" />
                </SubmitButton>
              </form>
            </FormBox>
          </RightColumn>
        </ContentContainer>
      </HeroContactWrapper>
    );
  }
}

HeroContactSection.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  options: PropTypes.object,
  containerClass: PropTypes.string,
  backgroundImage: PropTypes.string,
  phone: PropTypes.string,
  googleFormLink: PropTypes.string,
  intl: PropTypes.object,
};

HeroContactSection.defaultProps = {
  containerClass: '',
  backgroundImage: '',
  options: {},
};

export default injectIntl(HeroContactSection);
